import * as React from "react";
import styled from "styled-components";
import Box from "@sproutsocial/seeds-react-box";
import {
  COMMON,
  FLEXBOX,
  BORDER,
  LAYOUT,
} from "@sproutsocial/seeds-react-system-props";
import type { TypeModalBodyProps } from "../ModalTypes";

/**
 * Modal body component for the main content area.
 *
 * This component provides the scrollable content area of the modal with proper spacing
 * and overflow handling. It automatically takes up available space between the header
 * and footer, with vertical scrolling enabled when content exceeds available height.
 *
 * @example
 * <ModalBody>
 *   <Text>Your modal content goes here.</Text>
 *   <FormField label="Name" />
 * </ModalBody>
 */
const StyledModalBody = styled(Box)`
  font-family: ${(props) => props.theme.fontFamily};
  overflow-y: auto;
  flex: 1 1 auto;
  padding: ${(props) => `${props.theme.space[400]}`};
  ${(props) => props.theme.typography[300]}
  color: ${(props) => props.theme.colors.text.body};
  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    flex-basis: 100%;
  }

  ${COMMON}
  ${FLEXBOX}
  ${BORDER}
  ${LAYOUT}
`;

StyledModalBody.displayName = "ModalBody";

export const ModalBody = React.forwardRef<HTMLDivElement, TypeModalBodyProps>(
  ({ children, ...rest }, ref) => {
    return (
      <StyledModalBody
        data-slot="modal-body"
        data-qa-modal-body
        ref={ref}
        {...rest}
      >
        {children}
      </StyledModalBody>
    );
  }
);

ModalBody.displayName = "ModalBody";
